// Ödeme alındı, eSIM hazırlanıyor — /order/pending
//
// Sınırsız paketlerde sağlayıcı hattı senkron açmıyor; capture yanıtı
// success=true + status="PENDING" döner ve eSIM birkaç dakika sonra hazır olur
// (src/FRONTEND_INTEGRATION.md §5.3). Bu ekran o aralığı yönetir:
// PENDING iken sipariş /my-esims listesinde HENÜZ GÖRÜNMEZ, bu yüzden
// "bulunamadı" bir hata değil "hazırlanıyor" demektir.

import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCircleCheck,
  faClock,
  faArrowRight,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { getMyEsims } from '../account/accountApi';
import { formatValidity } from '../../utils/formatters';
import './ordersuccess.css';

// Doküman önerisi: 10 saniyede bir, en fazla ~5 dakika.
const POLL_MS = 10000;
const MAX_ATTEMPTS = 30;

const OrderPending = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { orderId, message, plan = {} } = location.state || {};
  const [timedOut, setTimedOut] = useState(false);
  const [found, setFound] = useState(false);
  // Bulunduktan sonra yönlendirme yapılırken yeni yoklama başlamasın.
  const doneRef = useRef(false);

  useEffect(() => {
    if (!orderId || doneRef.current) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async (attempt) => {
      if (cancelled || doneRef.current) return;
      try {
        const list = await getMyEsims();
        const esim = (list || []).find((e) => e.orderId === orderId);
        if (esim) {
          doneRef.current = true;
          if (cancelled) return;
          setFound(true);
          navigate(`/account/esim/${orderId}`, { replace: true, state: { esim } });
          return;
        }
      } catch {
        // Ağ/oturum hatasında yoklamayı durdurma; bir sonraki turda tekrar dene.
      }
      if (cancelled) return;
      const next = attempt + 1;
      if (next >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(() => poll(next), POLL_MS);
    };

    timer = setTimeout(() => poll(0), POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Yoklama döngüsü bir kez kurulur; attempts state'i yalnızca gösterim için.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Ödeme akışı dışından gelinemez.
  if (!orderId) return <Navigate to="/account" replace />;

  const validityText = formatValidity(t, plan.validity);

  return (
    <div className="order">
      <StoreHeader />

      <main className="order-main">
        <div className="order-hero">
          <span className={`order-check ${found ? '' : 'order-check--pending'}`}>
            <FontAwesomeIcon icon={found ? faCircleCheck : faClock} />
          </span>
          <h1 className="order-congrats">
            {found ? t('order.pending.ready') : t('order.pending.title')}
          </h1>
          {/* Backend'in kendi mesajı varsa onu göster (dile göre gelir). */}
          <p className="order-message">{message || t('order.pending.message')}</p>
        </div>

        <section className="order-section">
          <h2 className="order-section-title">{t('order.details')}</h2>
          <div className="order-summary">
            {plan.flag ? (
              <img className="order-summary-flag" src={plan.flag} alt="" />
            ) : (
              <span className="order-summary-flag order-summary-flag--fallback">
                <FontAwesomeIcon icon={faGlobe} />
              </span>
            )}
            <div>
              <span className="order-summary-name">
                {plan.countryName}
                {plan.dataAmount ? ` · ${plan.dataAmount}` : ''}
              </span>
              {validityText && (
                <span className="order-summary-validity">
                  {t('detail.validityLabel')}: {validityText}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="order-section">
          {timedOut ? (
            <p className="order-pending-note">{t('order.pending.timeout')}</p>
          ) : (
            <p className="order-pending-note">
              <FontAwesomeIcon icon={faSpinner} spin /> {t('order.pending.waiting')}
            </p>
          )}
        </section>

        <Link to="/account" className="order-cta">
          {t('order.pending.cta')}
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </main>

      <Footer />
    </div>
  );
};

export default OrderPending;
